import { NavLink, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";
import { useBranding } from "@/context/BrandingContext";
import {
  LayoutDashboard, Sparkles, Building2, FileSignature, Receipt,
  FolderKanban, Clock, CheckCircle, Users, Truck, Box, Map, Sparkle, Settings as SettingsIcon,
  Compass, Calculator, Handshake, Banknote, Inbox, BarChart3, Gauge, ListChecks, History,
  UserPlus, Wallet, Folder, Globe, UserCircle, Shield, Palette, Server, Trash2, Database, CheckSquare,
} from "lucide-react";

const ICONS = {
  "layout-dashboard": LayoutDashboard,
  sparkles: Sparkles,
  "building-2": Building2,
  "file-signature": FileSignature,
  receipt: Receipt,
  "folder-kanban": FolderKanban,
  clock: Clock,
  "check-circle": CheckCircle,
  users: Users,
  truck: Truck,
  box: Box,
  map: Map,
  sparkle: Sparkle,
  settings: SettingsIcon,
  calculator: Calculator,
  handshake: Handshake,
  banknote: Banknote,
  inbox: Inbox,
  "chart-gantt": BarChart3,
  gauge: Gauge,
  "list-checks": ListChecks,
  history: History,
  "user-plus": UserPlus,
  wallet: Wallet,
  folder: Folder,
  globe: Globe,
  "user-circle": UserCircle,
  "bar-chart-3": BarChart3,
  shield: Shield,
  palette: Palette,
  server: Server,
  "trash-2": Trash2,
  database: Database,
  "check-square": CheckSquare,
};

export default function Sidebar({ collapsed = false }) {
  const location = useLocation();
  const { branding, version } = useBranding();
  const { data: groups = [] } = useQuery({
    queryKey: ["nav"],
    queryFn: async () => (await api.get("/meta/nav")).data,
  });

  const companyName = branding?.company_name || "Dream Home ERP";
  const versionLabel = version?.version || branding?.app_version || "";

  return (
    <aside
      className={cn(
        "border-r border-zinc-200 bg-white flex flex-col h-full transition-[width] duration-200",
        collapsed ? "w-16" : "w-64",
      )}
      data-testid="app-sidebar"
    >
      <div className="h-16 flex items-center gap-3 px-5 border-b border-zinc-200 shrink-0">
        {branding?.logo_url ? (
          <img src={branding.logo_url} alt="logo" className="w-8 h-8 rounded-md object-contain bg-white" />
        ) : (
          <div className="w-8 h-8 rounded-md bg-zinc-900 text-white grid place-items-center">
            <Compass className="w-4 h-4" />
          </div>
        )}
        {!collapsed && (
          <div className="leading-tight min-w-0">
            <div className="text-[13px] font-heading font-bold tracking-tight truncate" data-testid="sidebar-brand-name">{companyName}</div>
            <div className="text-[11px] text-zinc-500 uppercase tracking-widest truncate">Business Operating System</div>
          </div>
        )}
      </div>
      <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-6" data-testid="sidebar-nav">
        {groups.map((g) => (
          <div key={g.id} data-testid={`nav-group-${g.id}`}>
            {!collapsed && (
              <div className="px-2 mb-2">
                <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-zinc-400">
                  {g.title}
                </div>
                <div className="text-[10px] text-zinc-400">Owner · {g.owner}</div>
              </div>
            )}
            <ul className="space-y-0.5">
              {g.items.map((it) => {
                const Icon = ICONS[it.icon] || LayoutDashboard;
                const active =
                  location.pathname === it.path ||
                  (it.path !== "/app" && location.pathname.startsWith(it.path));
                return (
                  <li key={it.key}>
                    <NavLink
                      to={it.path}
                      end={it.path === "/app"}
                      data-testid={`sidebar-link-${it.key}`}
                      className={cn(
                        "flex items-center gap-3 rounded-md px-2 py-1.5 text-sm text-zinc-700 hover:bg-zinc-100 transition-colors",
                        active && "bg-zinc-900 text-white hover:bg-zinc-900",
                      )}
                    >
                      <Icon className="w-4 h-4 shrink-0" />
                      {!collapsed && (
                        <span className="flex-1 truncate">{it.label}</span>
                      )}
                      {!collapsed && it.coming_soon && (
                        <span
                          className={cn(
                            "text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded",
                            active ? "bg-white/20 text-white" : "bg-zinc-100 text-zinc-500 border border-zinc-200",
                          )}
                        >
                          {it.sprint || "soon"}
                        </span>
                      )}
                    </NavLink>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </nav>
      {!collapsed && (
        <div className="p-4 border-t border-zinc-200 text-[11px] text-zinc-500 shrink-0 space-y-0.5" data-testid="sidebar-footer">
          <div className="font-heading font-semibold text-zinc-700 truncate">{companyName}</div>
          <div className="flex items-center gap-2">
            <span className="font-mono text-zinc-600" data-testid="sidebar-version">{versionLabel}</span>
            {versionLabel && <span className="inline-flex items-center gap-1 text-emerald-600">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> live
            </span>}
          </div>
        </div>
      )}
    </aside>
  );
}
